import { motion, AnimatePresence } from "framer-motion";
import { TeacherPresence as PresenceState } from "@/harness/harness.types";
import { cn } from "@/lib/utils";

const LABEL: Record<PresenceState, string> = {
  idle: "",
  thinking: "Thinking",
  speaking: "Speaking",
  writing: "Writing",
  listening: "Listening — your turn",
  celebrating: "Nice work",
  correcting: "Let's look again",
};

/**
 * The teacher's presence indicator — visible even when silent, per the
 * realism spec. This is deliberately NOT a chat "typing..." bubble: each
 * state gets its own motion signature so idle vs. listening vs. thinking
 * read as distinct at a glance, not just a color swap.
 */
export function TeacherPresence({ presence }: { presence: PresenceState }) {
  return (
    <div className="glass flex items-center gap-3 rounded-2xl px-4 py-3">
      <div className="relative h-3 w-3">
        <AnimatePresence mode="wait">
          {presence === "thinking" && (
            <motion.div
              key="thinking"
              className="flex gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="thinking-dot h-1.5 w-1.5 rounded-full bg-marker"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </motion.div>
          )}
          {presence === "listening" && (
            <motion.span
              key="listening"
              className="block h-3 w-3 rounded-full bg-highlight"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
          )}
          {(presence === "speaking" || presence === "writing") && (
            <motion.span
              key="active"
              className="block h-3 w-3 rounded-full bg-marker"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.9, repeat: Infinity }}
            />
          )}
          {presence === "idle" && (
            <motion.span key="idle" className="block h-3 w-3 rounded-full bg-border" />
          )}
          {presence === "celebrating" && (
            <motion.span
              key="celebrating"
              className="block h-3 w-3 rounded-full bg-correct"
              animate={{ scale: [1, 1.6, 1] }}
              transition={{ duration: 0.5 }}
            />
          )}
          {presence === "correcting" && (
            <motion.span key="correcting" className="block h-3 w-3 rounded-full bg-incorrect" />
          )}
        </AnimatePresence>
      </div>
      <span className={cn("font-body text-sm text-ink/70", presence === "idle" && "opacity-0")}>
        {LABEL[presence]}
      </span>
    </div>
  );
}
